import { ModuleRendering, SlotRendering } from '@frontend/common-types';
import { DefaultPrimaryButton, DefaultSecondaryButton, Spinner } from '@frontend/components/common';
import { CommonMessage } from '@frontend/lang';
import { SlotRendering as SlotTypesSlotRendering } from '@frontend/slot/types';
import { SpotRenderingClient } from '@frontend/spot/api';
import { FormattedMessage } from 'react-intl';

import { ChooseModuleModal } from '../../components/choose-module-modal/choose-module-modal.component';
import { KioskNavigationFooter } from '../../components/kiosk-navigation-footer.component';
import { KioskNavigationHeader } from '../../components/kiosk-navigation-header.component';
import { SpotLayout } from '../../spot-rendering';
import { useKioskNavigate } from '../../utils/kiosk-navigation';
import { useViewContent } from './view-content.controller';

const ID = 'view-content';

const ViewContent = () => {
    const navigate = useKioskNavigate();
    const viewProps = useViewContent();
    const showMainContent = !viewProps.isLoading && !viewProps.error;

    return (
        <div
            id={ID}
            className='bg-background-default flex size-full flex-col items-center justify-start overflow-hidden'>
            <KioskNavigationHeader
                title={CommonMessage.DETAIL.MANAGE(CommonMessage.OBJECTS.SPOT.DISTRI_SPOT)}
                onBack={() => navigate(-1)}
            />

            <div className='flex size-full flex-1 flex-col items-center justify-center overflow-hidden p-6'>
                {viewProps.isLoading && <Spinner />}
                {viewProps.error && <div className='text-status-danger'>{viewProps.error}</div>}
                {showMainContent && (
                    <div className='flex size-full w-full flex-1 flex-col items-start gap-6 overflow-y-auto pb-4'>
                        <div className='space-y-4'>
                            <p className='text-foreground-default w-full text-lg font-semibold'>
                                <FormattedMessage
                                    id='kiosk.manage-spot.links.utilities'
                                    defaultMessage='Utilities'
                                />
                                {': '}
                                <FormattedMessage
                                    id='kiosk.manage-spot.links.view-content'
                                    defaultMessage='View content'
                                />
                            </p>
                            <div className='text-foreground-default w-full text-base font-medium'>
                                {viewProps.selectedSlot ? (
                                    <FormattedMessage
                                        id='kiosk.manage-spot.selected-slot'
                                        defaultMessage='Selected {slot}'
                                        values={{
                                            slot: viewProps.selectedSlot.display ?? viewProps.selectedSlot.number ?? ''
                                        }}
                                    />
                                ) : (
                                    <FormattedMessage
                                        id='kiosk.manage-spot.select-slot'
                                        defaultMessage='Select a SLOT'
                                    />
                                )}
                            </div>
                        </div>

                        <div className='flex w-full flex-col items-center gap-4'>
                            {viewProps.currentModule && viewProps.accountId && viewProps.spotId && (
                                <div className='flex size-full max-w-md flex-row justify-center'>
                                    <SpotLayout<ModuleRendering, SlotRendering>
                                        key={viewProps.currentModule.id}
                                        renderMode='module'
                                        module={viewProps.currentModule}
                                        fetchSpotData={(arg) => SpotRenderingClient.fetchSpotRendering(viewProps.accountId!, viewProps.spotId!, arg)}
                                        fetchModuleData={(accountId, spotId, moduleId) =>
                                            SpotRenderingClient.fetchSpotModuleRendering(accountId, spotId, moduleId)
                                        }
                                        slot={{
                                            renderElement: (slot) =>
                                                viewProps.slotComponent({
                                                    ...slot,
                                                    slot_ids: [slot.id],
                                                    column_slot_ids: null
                                                } as unknown as SlotTypesSlotRendering)
                                        }}
                                        enableSlotHover={false}
                                        onModuleNameClick={() => viewProps.setSelectModuleModalOpen(true)}
                                        onSelectedModuleChange={(moduleId) => {
                                            const module = viewProps.modules?.find((m) => m.id === moduleId);
                                            if (module) viewProps.setCurrentModule(module);
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            <KioskNavigationFooter
                leftButton={
                    <DefaultSecondaryButton
                        text={CommonMessage.BUTTONS.GO_BACK}
                        onClick={() => navigate(-1)}
                    />
                }
                rightButton={
                    <DefaultPrimaryButton
                        text={
                            <FormattedMessage
                                id='kiosk.manage-spot.view-content-layout.view-slot-details'
                                defaultMessage='View SLOT details'
                            />
                        }
                        onClick={() => viewProps.selectedSlot && navigate(`/spots/layout/slot/${viewProps.selectedSlot.id}`)}
                        disabled={!showMainContent || !viewProps.selectedSlot}
                    />
                }
            />

            {showMainContent && (
                <ChooseModuleModal
                    modules={viewProps.modules}
                    show={viewProps.selectModuleModalOpen}
                    onClose={() => viewProps.setSelectModuleModalOpen(false)}
                    onSelectModule={viewProps.setCurrentModule}
                    currentModule={viewProps.currentModule}
                />
            )}
        </div>
    );
};

export default ViewContent;
